import { BorderBeam, Button, Popover, Tooltip } from 'antd'
import { Sparkles } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import type {
  ChecklistClickTarget,
  ChecklistDismissReason,
  ChecklistItem
} from 'src/analytics'
import { ChecklistClickTarget as ClickTarget, track } from 'src/analytics'
import { ContinueBootScreen } from 'src/components'
import { alignAnimationsToPageClock } from 'src/utils/animationClock'

import { clearChecklistIntro, peekChecklistIntro } from '../welcomeHandoff'

import { ChecklistPanel } from './ChecklistPanel'
import {
  BEAM_VISIBLE_MS,
  CHECKLIST_INTRO_DELAY_MS,
  SNOOZE_DAYS
} from './constants'
import { DismissReasonModal } from './DismissReasonModal'
import { useGettingStarted } from './hooks'
import { useStyles } from './styles'
import type { ChecklistItemModel } from './types'

type Props = {
  collapsed: boolean
  workspaceId: number | undefined
  resolvePath: (menuKey: string) => string | undefined
}

/**
 * The setup nudge in the sidebar footer: a pill with the progress counter, and a
 * two-pane popover behind it.
 *
 * Renders nothing at all when there is nothing to nudge about — no workspace, no
 * task this user may open, snoozed, dismissed, or finished.
 */
export const GettingStartedPill = ({
  collapsed,
  workspaceId,
  resolvePath
}: Props) => {
  const { styles, cx } = useStyles()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const {
    isLoading,
    visible,
    audience,
    items,
    completedCount,
    total,
    complete,
    hideForNow,
    hideForGood
  } = useGettingStarted({ workspaceId, resolvePath })

  const [open, setOpen] = useState(false)
  const [dismissOpen, setDismissOpen] = useState(false)
  /** List-only: how the panel opens, until a hover or a Tab asks for the card. */
  const [compact, setCompact] = useState(false)
  // Null means "whatever is next" — the first unfinished task, recomputed on
  // every open so a returning user lands on what is actually left.
  const [activeKey, setActiveKey] = useState<ChecklistItem | null>(null)

  // The beam is a "look here", not a decoration — it retires a minute in.
  const [beamOn, setBeamOn] = useState(true)
  const beamHostRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const timer = window.setTimeout(() => setBeamOn(false), BEAM_VISIBLE_MS)

    return () => window.clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (beamHostRef.current) {
      alignAnimationsToPageClock(beamHostRef.current)
    }
  }, [visible, collapsed])

  /**
   * The workspace was created a moment ago and the checklist has not been opened
   * yet, so it opens itself — once, as a list, once the welcome is off screen.
   *
   * Read on every render rather than captured at mount: `workspaceId` arrives
   * with the session, which can still be resolving when this first renders. The
   * marker is spent in the timer instead, so StrictMode's mount-cleanup-mount
   * cannot swallow the intro the way consuming it here would.
   */
  const introOwed = workspaceId !== undefined && peekChecklistIntro(workspaceId)

  useEffect(() => {
    if (!introOwed || workspaceId === undefined) return

    const timer = window.setTimeout(() => {
      clearChecklistIntro(workspaceId)
      setCompact(true)
      setActiveKey(null)
      setOpen(true)
      // No `onboarding_checklist_expanded`: that event counts a decision to
      // look, and an automatic open would inflate it in every new workspace.
    }, CHECKLIST_INTRO_DELAY_MS)

    return () => window.clearTimeout(timer)
  }, [introOwed, workspaceId])

  if (isLoading) return <ContinueBootScreen />

  if (!visible) return null

  const nextKey = (items.find((item) => !item.done) ?? items[0]).def.key
  const active = activeKey ?? nextKey

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    // Every open starts as the list: the card is a reading pane for one task,
    // and which task that is only becomes clear once a row is picked. Closing
    // puts the panel back to the list for next time.
    setCompact(next)

    if (next) {
      setActiveKey(null)
      track('onboarding_checklist_expanded', {
        audience,
        completed_count: completedCount,
        total_count: total
      })

      return
    }

    track('onboarding_checklist_collapsed', { audience })
  }

  const handleActivate = (key: ChecklistItem) => {
    if (key === active) return

    setActiveKey(key)
    track('onboarding_checklist_switched', { audience, item: key })
  }

  const handleOpenItem = (
    item: ChecklistItemModel,
    target: ChecklistClickTarget
  ) => {
    track('onboarding_checklist_item_clicked', {
      audience,
      item: item.def.key,
      target
    })

    if (target === ClickTarget.DOCS) return

    // Only the in-app trip counts as having done the task — reading the docs is
    // reading the docs. `complete` is a no-op for every step the platform can
    // see finish on its own; see `closedByOpening`.
    complete(item)
    setOpen(false)
    navigate(item.path)
  }

  const handleHideForNow = () => {
    setOpen(false)
    track('onboarding_checklist_hidden', { audience, snooze_days: SNOOZE_DAYS })
    hideForNow()
  }

  const handleDismiss = (reason: ChecklistDismissReason) => {
    setDismissOpen(false)
    track('onboarding_checklist_dismissed', { audience, reason })
    hideForGood(reason)
  }

  const title = t('shell.gettingStarted.title')
  const counter = `${completedCount}/${total}`

  const trigger = (
    <Button
      type="text"
      className={cx(styles.pill, collapsed && styles.pillCollapsed)}
      icon={<Sparkles size={14} />}
      aria-label={collapsed ? `${title} — ${counter}` : undefined}
    >
      {!collapsed && (
        <span className={styles.pillLabel}>
          <span>{title}</span>
          <span className={styles.pillCount}>{counter}</span>
        </span>
      )}
    </Button>
  )

  return (
    <>
      {/*
        `size` must stay well under twice the pill's height (30) or the beam
        covers the top and bottom edges at once and reads as a glow instead of a
        travelling line. `outset={0}` keeps it on the border, since the sidebar
        footer has only 4px of room around it. Colour is left to the theme:
        BorderBeam defaults to `colorPrimary`.
      */}
      <BorderBeam outset={0} size={40} duration={5}>
        <div
          ref={beamHostRef}
          className={cx(
            styles.beamHost,
            collapsed && styles.beamHostCollapsed,
            !beamOn && styles.beamOff
          )}
        >
          <Popover
            open={open}
            onOpenChange={handleOpenChange}
            trigger="click"
            // Above the pill, left edges aligned: the panel is wider than the
            // rail, so it spills over the page to the right rather than pushing
            // the sidebar.
            placement="topLeft"
            arrow={false}
            // The panel draws its own padding and a divider that must reach the
            // popover's edges.
            styles={{ container: { padding: 0 }, content: { padding: 0 } }}
            content={
              <ChecklistPanel
                compact={compact}
                onLeaveCompact={() => setCompact(false)}
                items={items}
                completedCount={completedCount}
                activeKey={active}
                onActivate={handleActivate}
                onOpen={handleOpenItem}
                onHideForNow={handleHideForNow}
                onDismissRequest={() => {
                  setOpen(false)
                  setDismissOpen(true)
                }}
              />
            }
          >
            {collapsed ? (
              // The label is the only thing lost to the collapsed rail, so it
              // comes back as a tooltip — but not while the panel it would cover
              // is open.
              <Tooltip
                title={open ? undefined : `${title} — ${counter}`}
                placement="right"
              >
                {trigger}
              </Tooltip>
            ) : (
              trigger
            )}
          </Popover>
        </div>
      </BorderBeam>

      <DismissReasonModal
        open={dismissOpen}
        onCancel={() => setDismissOpen(false)}
        onChoose={handleDismiss}
      />
    </>
  )
}
